import Decimal from 'decimal.js';
import { calculateInsurancePayment } from './calculateInsurancePayment';
import { calculatePatientResponsibility } from './calculatePatientResponsibility';
import { calculatePayorResponsibilityPerServiceNew } from './calculatePayorResponsibilityPerServiceNew';
import { PatientTransactionItemType, clog } from '@chiroup/core';
import { createDecimal } from './createDecimal';

export type CalculationPayor = {
  payorId: number;
  billingPriority: number;
  serviceAmounts: { code: string; amount: number }[] | Record<string, number>;
  coPay?: number;
  coInsurance?: number;
  deductible?: number;
  payorName: string;
  billedAmounts?: { [key: string]: number };
};

type Responsibilities = {
  patientResponsibilities: Record<number, string>;
  payorResponsibilities: Record<number, { amount: string; name: string }>;
  finalPatientResponsibility: string;
};

type PayorServiceDetail = {
  totalServices: Decimal;
  coPay: Decimal;
  coInsurance: Decimal;
  deductible: Decimal;
};

type PayorsResponsibilitiesPerService = {
  [payorId: string]: {
    code: string;
    amount: number;
  }[];
};

export const calculateResponsibilities = ({
  payors,
  totalChargedByClinic,
  services,
  courtesyBilling = false,
}: {
  payors: CalculationPayor[];
  totalChargedByClinic: { [key: string]: Decimal };
  services: PatientTransactionItemType[];
  courtesyBilling?: boolean;
}): Responsibilities => {
  const sortedPayors = payors.sort(
    (a, b) => a.billingPriority - b.billingPriority,
  );

  const highestAllowedAmountsPerService = findHighestAllowedAmounts(payors);

  let payorsResponsibilitiesPerService: PayorsResponsibilitiesPerService = {};
  let courtesyBillingAmount = '0.00';

  return sortedPayors.reduce(
    (acc: Responsibilities, payor) => {
      const payorCopy = JSON.parse(JSON.stringify(payor));
      const payorIdStr = payorCopy.payorId.toString();
      const totalServices = calculateTotalServices(
        payorCopy.serviceAmounts as Record<string, number>,
      );
      const orderedServiceAmounts: { code: string; amount: number }[] =
        orderServices(services, payorCopy);

      payorCopy.serviceAmounts = orderedServiceAmounts;

      const payorServiceDetail: PayorServiceDetail = {
        totalServices,
        coPay: createDecimal(payorCopy.coPay || 0),
        coInsurance: createDecimal(payorCopy.coInsurance || 0),
        deductible: createDecimal(payorCopy.deductible || 0),
      };

      let responsibilities: Responsibilities = {
        patientResponsibilities: {},
        payorResponsibilities: {},
        finalPatientResponsibility: '0.00',
      };
      if (payorCopy.billingPriority === 1) {
        payorsResponsibilitiesPerService = getPayorResponsibilityPerService({
          payorsResponsibilitiesPerService,
          payor: payorCopy,
          services,
        });

        const totalPayorResponsibility = Object.values(
          payorsResponsibilitiesPerService[payorIdStr],
        )
          .reduce((total, service) => {
            return total.plus(service.amount);
          }, createDecimal(0))
          .toNumber()
          .toFixed(2);

        responsibilities = calculateForPrimaryPayor(
          payorServiceDetail,
          payorCopy,
        );
        responsibilities.payorResponsibilities[payorCopy.payorId].amount =
          totalPayorResponsibility;
      } else {
        if (isMedicarePayor(payorCopy.payorName)) {
          const medicareResponsibilities = newMedicareCalculateResponsibilities(
            {
              highestAllowedAmountPerService: highestAllowedAmountsPerService,
              payorsResponsibilitiesPerService,
              payor: payorCopy,
              totalChargedByClinic,
              payorServiceDetail,
              services,
            },
          );

          const {
            patientResponsibilities,
            payorResponsibilities,
            finalPatientResponsibility,
          } = medicareResponsibilities;
          responsibilities.patientResponsibilities[payorCopy.payorId] =
            patientResponsibilities[payorCopy.payorId];
          responsibilities.payorResponsibilities[payorCopy.payorId] =
            payorResponsibilities[payorCopy.payorId];
          responsibilities.finalPatientResponsibility =
            finalPatientResponsibility;
        } else {
          const serviceAmounts = payorCopy.serviceAmounts;
          responsibilities = calculateForOtherPayor(
            payorServiceDetail,
            payorCopy,
            serviceAmounts,
            payorsResponsibilitiesPerService,
            sortedPayors,
            services,
          );
        }
      }

      if (courtesyBilling) {
        if (payorCopy.billingPriority === 1) {
          const amounts: number[] = Object.values(totalChargedByClinic).map(
            (amount) => Number(amount),
          );
          courtesyBillingAmount = amounts
            .reduce(
              (total: Decimal, amount: number) => {
                return total.plus(createDecimal(amount || 0));
              },
              createDecimal(0) as Decimal,
            )
            .toFixed(2);
        }

        responsibilities.finalPatientResponsibility = '0.00';
        responsibilities.finalPatientResponsibility = courtesyBillingAmount;
        responsibilities.payorResponsibilities = Object.entries(
          responsibilities.payorResponsibilities,
        ).reduce(
          (acc, [key, value]) => {
            acc[key] = { amount: '0.00', name: value.name };
            return acc;
          },
          {} as Record<string, { amount: string; name: string }>,
        );
      }

      return {
        patientResponsibilities: {
          ...acc.patientResponsibilities,
          ...responsibilities.patientResponsibilities,
        },
        payorResponsibilities: {
          ...acc.payorResponsibilities,
          ...responsibilities.payorResponsibilities,
        },
        finalPatientResponsibility: responsibilities.finalPatientResponsibility,
      };
    },
    {
      patientResponsibilities: {},
      payorResponsibilities: {},
      finalPatientResponsibility: '0.00',
    },
  );
};

const calculateTotalServices = (
  serviceAmounts: Record<string, number>,
): Decimal =>
  Object.values(serviceAmounts).reduce((total, amount) => {
    return total.plus(createDecimal(amount));
  }, createDecimal(0));

const isMedicarePayor = (payorName: string): boolean =>
  payorName.toLowerCase().includes('medicare');

const calculateForPrimaryPayor = (
  { totalServices, coPay, coInsurance, deductible }: PayorServiceDetail,
  { payorId, payorName }: CalculationPayor,
): Responsibilities => {
  // Convert input values to numbers and calculate patient responsibility
  const patientResponsibility = calculatePatientResponsibility({
    totalServices: totalServices.toNumber(),
    coPay: coPay.toNumber(),
    coInsurance: coInsurance.toNumber(),
    deductible: deductible.toNumber(),
  });

  // Convert patient responsibility to string once for reuse
  const patientResponsibilityStr = patientResponsibility.toString();

  return {
    patientResponsibilities: {
      [payorId]: patientResponsibilityStr,
    },
    payorResponsibilities: {
      [payorId]: {
        amount: '0', //TODO: this is here because of the way the function used to work.  This doesnt appear to break anything, but it could be better
        name: payorName,
      },
    },
    finalPatientResponsibility: patientResponsibilityStr,
  };
};

type ServiceAmount = { code: string; amount: number };
type AdjustedServiceAmount = ServiceAmount; // Alias for clarity, but could be extended or modified in the future

const calculateAdjustedServiceAmounts = ({
  serviceAmounts,
  payorsResponsibilitiesPerService,
  payor,
  payors,
  services,
}: {
  serviceAmounts: ServiceAmount[];
  payorsResponsibilitiesPerService: PayorsResponsibilitiesPerService;
  payor: CalculationPayor;
  payors: CalculationPayor[];
  services: PatientTransactionItemType[];
}): Decimal => {
  // Sum the service amounts from payors with higher billing priority
  const summedServiceAmounts = new Map<string, Decimal>();
  payors
    .filter((p) => p.billingPriority < payor.billingPriority)
    .forEach((p) => {
      payorsResponsibilitiesPerService[p.payorId.toString()]?.forEach(
        (service) => {
          const currentAmount =
            summedServiceAmounts.get(service.code) || new Decimal(0);
          summedServiceAmounts.set(
            service.code,
            currentAmount.plus(service.amount),
          );
        },
      );
    });

  // Adjust the service amounts based on summed responsibilities
  const adjustedServiceAmounts: AdjustedServiceAmount[] = serviceAmounts.map(
    (service) => {
      const totalResponsibility =
        summedServiceAmounts.get(service.code) || new Decimal(0);
      let adjustedAmount = new Decimal(service.amount).minus(
        totalResponsibility,
      );
      if (adjustedAmount.lessThan(0)) adjustedAmount = new Decimal(0);

      return { code: service.code, amount: adjustedAmount.toNumber() };
    },
  );
  payorsResponsibilitiesPerService[payor.payorId.toString()] = orderServices(
    services,
    {
      ...payor,
      serviceAmounts: adjustedServiceAmounts,
    },
  );

  // Calculate the total adjusted amount
  return adjustedServiceAmounts.reduce(
    (total, { amount }) => total.plus(amount),
    new Decimal(0),
  );
};

const calculateForOtherPayor = (
  input: PayorServiceDetail,
  payor: CalculationPayor,
  serviceAmounts: { code: string; amount: number }[],
  payorsResponsibilitiesPerService: PayorsResponsibilitiesPerService,
  payors: CalculationPayor[],
  services: PatientTransactionItemType[],
): Responsibilities => {
  const adjustedTotalServices = calculateAdjustedServiceAmounts({
    serviceAmounts,
    payorsResponsibilitiesPerService,
    payor,
    payors,
    services,
  });

  // Calculate patient responsibility based on the adjusted total services
  const patientResponsibilityAmount = calculatePatientResponsibility({
    totalServices: adjustedTotalServices.toNumber(),
    coPay: input.coPay.toNumber(),
    coInsurance: input.coInsurance.toNumber(),
    deductible: input.deductible.toNumber(),
  });
  const amountsToPassIntoFunction = Object.entries(
    payorsResponsibilitiesPerService[payor.payorId.toString()],
  )?.reduce(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (acc, [_, { code, amount }]) => {
      acc.push({ code, amount });
      return acc;
    },
    [] as { code: string; amount: number }[],
  );

  calculatePayorResponsibilityPerServiceNew({
    payor: { ...payor, serviceAmounts: amountsToPassIntoFunction },
  });

  const payorResponsibilityResult = calculateInsurancePayment({
    totalServices: adjustedTotalServices.toNumber(),
    coPay: input.coPay.toNumber(),
    coInsurance: input.coInsurance.toNumber(),
    deductible: input.deductible.toNumber(),
  }).insurerTotal;

  const payorResponsibilityAmount = Math.min(
    +payorResponsibilityResult,
    adjustedTotalServices.toNumber(),
  );
  const adjustedPayorResponsibility = payorResponsibilityAmount.toFixed(2);

  return {
    patientResponsibilities: {
      [payor.payorId]: patientResponsibilityAmount,
    },
    payorResponsibilities: {
      [payor.payorId]: {
        amount: adjustedPayorResponsibility,
        name: payor.payorName,
      },
    },
    finalPatientResponsibility: patientResponsibilityAmount,
  };
};

const getPayorResponsibilityPerService = ({
  payorsResponsibilitiesPerService,
  payor,
  services,
}: {
  payorsResponsibilitiesPerService: PayorsResponsibilitiesPerService;
  payor: CalculationPayor;
  services: PatientTransactionItemType[];
}) => {
  const payorIdStr = payor.payorId.toString();
  payorsResponsibilitiesPerService[payorIdStr] =
    payorsResponsibilitiesPerService[payorIdStr] || [];

  const payorResponsibilitiesPerService =
    calculatePayorResponsibilityPerServiceNew({ payor });

  payorsResponsibilitiesPerService[payorIdStr] = orderServices(services, {
    ...payor,
    serviceAmounts: payorResponsibilitiesPerService,
  });
  return payorsResponsibilitiesPerService;
};

//TODO MEDICARE STUFF:
// As secondary payer, Medicare pays the lowest of the following amounts per service:
// 1.  The actual charge by the clinic minus the amount paid by the primary payer.
// 2.  The amount that Medicare would pay if the services were not covered by a primary payer.
// 3.  The higher of the Medicare fee schedule (without regard to any applicable Medicare deductible or coinsurance amounts)
//     or the primary payer's allowable charge (without regard to any deductible or co-insurance imposed by the policy or plan) minus the amount actually
//     paid by the primary payer

type ServiceDetails = {
  [serviceCode: string]: Decimal;
};

const newMedicareCalculateResponsibilities = ({
  highestAllowedAmountPerService,
  payorsResponsibilitiesPerService,
  payor,
  totalChargedByClinic,
  payorServiceDetail,
  services,
}: {
  highestAllowedAmountPerService: { [serviceCode: string]: number };
  payorsResponsibilitiesPerService: PayorsResponsibilitiesPerService;
  payor: CalculationPayor;
  totalChargedByClinic: { [key: string]: Decimal };
  payorServiceDetail: PayorServiceDetail;
  services: PatientTransactionItemType[];
}) => {
  // Calculate the total paid by service code
  const totalPaidByServiceCode: ServiceDetails = Object.values(
    payorsResponsibilitiesPerService,
  ).reduce(
    (totalPaid: { [key: string]: Decimal }, serviceDetails) => {
      serviceDetails.forEach((service) => {
        totalPaid[service.code] = totalPaid[service.code]
          ? totalPaid[service.code].plus(service.amount)
          : new Decimal(service.amount);
      });
      return totalPaid;
    },
    {} as { [key: string]: Decimal },
  );

  // Calculate the excess charge per service
  const excessChargePerService: ServiceDetails = Object.entries(
    totalChargedByClinic,
  ).reduce(
    (
      excessCharges: { [serviceCode: string]: Decimal },
      [serviceCode, totalCharged],
    ) => {
      const paidAmount =
        totalPaidByServiceCode[serviceCode] || createDecimal(0);
      const excessCharge = totalCharged.minus(paidAmount).greaterThan(0)
        ? totalCharged.minus(paidAmount)
        : createDecimal(0);

      excessCharges[serviceCode] = excessCharge;
      return excessCharges;
    },
    {} as { [serviceCode: string]: Decimal },
  );

  // Calculate responsibilities based on highestAllowedAmountPerService and amount already paid by service code
  const payorResponsibilities = getPayorResponsibilityPerService({
    payorsResponsibilitiesPerService: {},
    payor,
    services,
  });

  const medicareWouldPayPerService = Object.values(
    payorResponsibilities,
  )[0].reduce(
    (acc: { [serviceCode: string]: Decimal }, curr) => {
      acc[curr.code] = createDecimal(curr.amount);
      return acc;
    },
    {} as { [serviceCode: string]: Decimal },
  );

  const highestAllowedAmountPerServiceResponsibilities = Object.entries(
    highestAllowedAmountPerService,
  ).reduce(
    (
      highestAllowedAmounts: { [serviceCode: string]: Decimal },
      [serviceCode, amount],
    ) => {
      highestAllowedAmounts[serviceCode] = createDecimal(amount).minus(
        totalPaidByServiceCode[serviceCode] || 0,
      );
      return highestAllowedAmounts as { [serviceCode: string]: Decimal };
    },
    {},
  );

  const medicareResponsibilityPerService = findLeastAmountPerService(
    medicareWouldPayPerService,
    excessChargePerService,
    highestAllowedAmountPerServiceResponsibilities,
  );

  payorsResponsibilitiesPerService[payor.payorId.toString()] = orderServices(
    services,
    {
      ...payor,
      serviceAmounts: medicareResponsibilityPerService,
    },
  );

  const totalMedicareResponsibility = Object.values(
    medicareResponsibilityPerService,
  ).reduce(
    (total, medicareResponsibilityPerService) =>
      total.plus(medicareResponsibilityPerService.amount),
    createDecimal(0),
  );

  const responsibilities = calculateForPrimaryPayor(
    { ...payorServiceDetail, totalServices: totalMedicareResponsibility },
    payor,
  );

  responsibilities.payorResponsibilities[payor.payorId].amount =
    totalMedicareResponsibility
      .minus(
        createDecimal(responsibilities.patientResponsibilities[payor.payorId]),
      )
      .lessThan(createDecimal(0))
      ? createDecimal(0).toFixed(2)
      : totalMedicareResponsibility
          .minus(
            createDecimal(
              responsibilities.patientResponsibilities[payor.payorId],
            ),
          )
          .toFixed(2);

  return responsibilities;
};

const findHighestAllowedAmounts = (payors: CalculationPayor[]) => {
  const highestAllowedAmounts: { [serviceCode: string]: number } = {};

  payors.forEach((payor) => {
    const serviceAmounts = payor.serviceAmounts as Record<string, number>;
    Object.keys(serviceAmounts).forEach((serviceCode) => {
      const amount = serviceAmounts[serviceCode];
      if (
        !highestAllowedAmounts[serviceCode] ||
        amount > highestAllowedAmounts[serviceCode]
      ) {
        highestAllowedAmounts[serviceCode] = amount;
      }
    });
  });

  return highestAllowedAmounts;
};

const findLeastAmountPerService = (
  medicareWouldPayPerService: { [serviceCode: string]: Decimal },
  excessChargePerService: { [serviceCode: string]: Decimal },
  highestAllowedAmountPerServiceResponsibilities: {
    [serviceCode: string]: Decimal;
  },
) => {
  const leastAmountPerServiceCode: { [serviceCode: string]: Decimal } = {};

  const allServiceCodes = new Set([
    ...Object.keys(medicareWouldPayPerService),
    ...Object.keys(excessChargePerService),
    ...Object.keys(highestAllowedAmountPerServiceResponsibilities),
  ]);

  allServiceCodes.forEach((serviceCode) => {
    const amounts = [
      medicareWouldPayPerService[serviceCode]
        ? new Decimal(medicareWouldPayPerService[serviceCode])
        : new Decimal(Infinity),
      excessChargePerService[serviceCode]
        ? new Decimal(excessChargePerService[serviceCode])
        : new Decimal(Infinity),
      highestAllowedAmountPerServiceResponsibilities[serviceCode]
        ? new Decimal(
            highestAllowedAmountPerServiceResponsibilities[serviceCode],
          )
        : new Decimal(Infinity),
    ];

    const leastAmount = amounts.reduce((min, current) =>
      Decimal.min(min, current),
    );

    if (!leastAmount.equals(new Decimal(Infinity))) {
      leastAmountPerServiceCode[serviceCode] = leastAmount;
    }
  });

  return Object.entries(leastAmountPerServiceCode).map(
    ([serviceCode, amount]) => ({
      code: serviceCode,
      amount: amount.lessThan(0) ? 0 : amount.toNumber(),
    }),
  );
};

const orderServices = (
  services: PatientTransactionItemType[],
  payor: CalculationPayor,
) => {
  const ordered: { code: string; amount: number }[] = [];
  if (!services || !services.length) {
    clog(`Order services: Called with no services.  Returning empty array.`);
    return ordered;
  }
  services.forEach((service) => {
    const code = service.code;

    if (code && payor.serviceAmounts && !Array.isArray(payor.serviceAmounts)) {
      const amount = (payor.serviceAmounts as Record<string, number | Decimal>)[
        code
      ];
      if (typeof amount === 'number') {
        ordered.push({ code, amount });
      } else if (amount instanceof Decimal) {
        ordered.push({ code, amount: amount.toNumber() });
      }
    } else if (service.amount && Array.isArray(payor.serviceAmounts)) {
      const service = (
        payor.serviceAmounts as { code: string; amount: number }[]
      ).find((service) => service.code === code);
      if (service) {
        ordered.push(service);
      }
    }
  });
  return ordered;
};
